// @ts-strict-ignore
import React from 'react'
import { Linking } from 'react-native'
import DeviceInfo from 'APP/Helpers/DeviceInfo'
import { useSelector } from 'APP/Hooks/redux'
import Moment from 'moment'
import Clipboard from '@react-native-clipboard/clipboard'
import Toast from 'react-native-toast-message'
import { useTheme } from 'styled-components/native'

// Services
import UrlBuilder from 'APP/Helpers/UrlBuilder'
import I18n from 'APP/I18n'
import Config from 'APP/Config'

// Components
import ListItem from 'APP/Converse/ListItem'
import List from 'APP/Components/List'
import NoticeBarContainer from 'APP/Components/NoticeBarContainer'
import DialogueBranding from 'APP/Components/DialogueBranding'

// Styling
import Styles from './style'
import { ResponsiveScrollView } from 'APP/Converse/Layout'
import { selectFamilyMembers } from 'APP/Store/Family/selectors'
import { FamilyMember } from 'APP/Store/Family/types'

interface Dependent {
  givenName: string
  familyName: string
}

const getFamilyChildrenList = (familyMembers: Record<string, FamilyMember>) => {
  return Object.values(familyMembers)
    .filter((familyMember) => familyMember.type === 'child')
    .map((child) => {
      return {
        givenName: child?.givenName,
        familyName: child?.familyName,
      }
    })
}

const LegalScreen = ({ navigation }) => {
  const theme = useTheme()
  const profile = useSelector((state) => state.patient.profile)
  const guardianGivenName = profile?.givenName
  const guardianFamilyName = profile?.familyName
  const consentDate = profile?.consentDate

  const includesRequiredService =
    profile?.eligibleServices &&
    Object.keys(profile?.eligibleServices)?.some(
      (service) => Config.COMMITMENT.requiredServices.indexOf(service) >= 0
    )
  const familyMembers = useSelector(selectFamilyMembers)

  const showPatientCharterUrl = includesRequiredService

  const openURL = (url: string) => {
    Linking.openURL(url)
  }

  const goToConsentScreen = () => {
    navigation.navigate('consentDocument', {
      uri: UrlBuilder.consentUrl(),
      title: I18n.t('LegalScreen.consent'),
    })
  }

  const goToCloseAccount = () => {
    navigation.navigate('closeAccount')
  }

  const gotoGuardianConsentScreen = (dependentGivenName: string, dependentFamilyName: string) => {
    navigation.navigate('guardianConsentDocument', {
      uri: UrlBuilder.guardianConsentUrl(
        guardianGivenName,
        guardianFamilyName,
        dependentGivenName,
        dependentFamilyName
      ),
    })
  }

  const renderDependent = (dependent: Dependent, i: number) => {
    const { givenName, familyName } = dependent
    return (
      <ListItem
        key={i}
        title={I18n.t('LegalScreen.guardianConsent')}
        subtitle={`${givenName} ${familyName}`}
        onPress={() => gotoGuardianConsentScreen(givenName, familyName)}
      />
    )
  }

  const displayGuardianConsents = () => {
    const dependentList = getFamilyChildrenList(familyMembers)
    return dependentList.map(renderDependent)
  }

  const appVersion = DeviceInfo.getVersion()

  return (
    <NoticeBarContainer containerStyle={{ paddingBottom: theme.insets.bottom }}>
      <ResponsiveScrollView alwaysBounceVertical={false}>
        <List containerStyle={Styles.listContainer}>
          {!!appVersion && (
            <ListItem
              key="appVersion"
              title={I18n.t('LegalScreen.version')}
              subtitle={appVersion}
              hideIconComponent
              onLongPress={() => {
                Clipboard.setString(appVersion)
                Toast.show({
                  text1: String(I18n.t('Common.copiedToClipboard')),
                  visibilityTime: 1000,
                })
              }}
            />
          )}
          <ListItem
            key="consent"
            title={I18n.t('LegalScreen.consent')}
            subtitle={Moment(consentDate)
              .format(String(I18n.t('LegalScreen.dateFormat')))
              .toString()}
            onPress={goToConsentScreen}
          />
          {displayGuardianConsents()}
          <ListItem
            key="closeAccount"
            title={I18n.t('LegalScreen.closeAccount')}
            onPress={goToCloseAccount}
          />
          {UrlBuilder.aboutUrl() && (
            <ListItem
              key="about"
              title={I18n.t('LegalScreen.about')}
              onPress={() => openURL(UrlBuilder.aboutUrl())}
              iconName="exit-to-app"
            />
          )}
          <ListItem
            key="privacyPolicy"
            title={I18n.t('LegalScreen.privacy')}
            onPress={() => openURL(UrlBuilder.privacyUrl())}
            iconName="exit-to-app"
          />
          <ListItem
            key="termsOfService"
            title={I18n.t('LegalScreen.terms')}
            onPress={() => openURL(UrlBuilder.termsUrl())}
            iconName="exit-to-app"
          />
          {showPatientCharterUrl && (
            <ListItem
              key="patientCharterUrl"
              title={I18n.t('LegalScreen.patientCharter')}
              onPress={() => openURL(UrlBuilder.patientCharterUrl())}
              iconName="exit-to-app"
            />
          )}
        </List>
      </ResponsiveScrollView>
      <DialogueBranding />
    </NoticeBarContainer>
  )
}

export default LegalScreen
